import { useState } from 'react';
import { Modal, Button, Input, UserSelect } from '../ui';
import { useKeepSetup } from '../../hooks/useGoogleKeep';

interface KeepSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function KeepSetupModal({ isOpen, onClose, onSuccess }: KeepSetupModalProps) {
  const [email, setEmail] = useState('');
  const [masterToken, setMasterToken] = useState('');
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const setupKeep = useKeepSetup();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setupKeep.mutate(
      { email, master_token: masterToken, collaborators },
      {
        onSuccess: () => {
          setEmail('');
          setMasterToken('');
          setCollaborators([]);
          onSuccess();
        },
      },
    );
  };

  const handleClose = () => {
    setEmail('');
    setMasterToken('');
    setCollaborators([]);
    setupKeep.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Connect Google Keep"
      size="md"
      footer={
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-sm)',
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!email || !masterToken || setupKeep.isPending}>
            {setupKeep.isPending ? 'Connecting...' : 'Connect'}
          </Button>
        </div>
      }
    >
      <div className="keep-setup-instructions">
        <p
          style={{
            marginBottom: 'var(--spacing-md)',
            color: 'var(--color-text-light)',
          }}
        >
          To connect Google Keep, you need a master token. This is a one-time setup:
        </p>
        <ol className="keep-setup-steps">
          <li className="keep-setup-step">
            Enable <strong>2-Step Verification</strong> on your Google account at{' '}
            <strong>myaccount.google.com/security</strong>
          </li>
          <li className="keep-setup-step">
            Create an <strong>App Password</strong> at <strong>myaccount.google.com/apppasswords</strong> — name it{' '}
            <em>Recipe Keeper</em> and copy the 16-character password
          </li>
          <li className="keep-setup-step">
            Run the token generation script:
            <pre className="keep-setup-command">
              {`pip install gpsoauth
python scripts/get_keep_token.py`}
            </pre>
          </li>
          <li className="keep-setup-step">
            Enter your Google email and App Password when prompted, then copy the master token output below
          </li>
        </ol>
        <p style={{ marginTop: 'var(--spacing-sm)', fontSize: '0.8rem', color: 'var(--color-text-light)' }}>
          If the script shows a <em>BadAuthentication</em> error, it will fall back to a browser-based flow — follow the
          on-screen instructions. See <strong>GOOGLE_KEEP.md</strong> in the project repo for detailed setup and
          troubleshooting.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--spacing-lg)' }}>
        <Input
          label="Google Email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@gmail.com"
          required
        />
        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <Input
            label="Master Token"
            type="password"
            value={masterToken}
            onChange={e => setMasterToken(e.target.value)}
            placeholder="Paste your master token here"
            required
          />
        </div>

        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <UserSelect
            label="Share notes with"
            selectedEmails={collaborators}
            onChange={setCollaborators}
            hint="Selected users will be added as collaborators on exported notes."
          />
        </div>

        {setupKeep.isError && (
          <div className="keep-error-message" style={{ marginTop: 'var(--spacing-md)' }}>
            {(setupKeep.error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
              'Failed to connect. Please check your credentials.'}
          </div>
        )}
      </form>
    </Modal>
  );
}
